import { CollectionQueryParams, ID } from './common';
import type { ReportEventDetail, ReportEventType } from './report';
import { SourceType } from './source';

export type BoundingBox = [number, number, number, number];

export interface Frame {
    id: number;
    time: number;
    bbox: BoundingBox;
}

export enum EventPromptStatus {
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
}

export enum EventViewStatus {
    LOADING = 'loading',
    ERROR = 'error',
    READY = 'ready',
}

export enum EventViewErrorReason {
    NOT_FOUND = 'notFound',
    TIMEOUT = 'timeout',
    FAILED = 'failed',
    PROCESSING = 'processing',
}

export interface Event {
    id: ID;
    createdAt: number;
    updatedAt: number;
    frames: Frame[];
    snapshotFrameId: number;
    description?: string;
    classId: number;
    sourceId: ID;
    sourceName?: string;
    sourceType?: SourceType;
    thumbnailUrl?: string;
    focuses?: number[][];
    // 'SCENE' = whole-scene event (no tracked object); 'OBJECT' = object event.
    type?: 'OBJECT' | 'SCENE';
    // Scene events only: in-video time window (seconds) of the scene vector.
    startTime?: number;
    endTime?: number;
}

export interface EventsPrompts {
    eventId: string;
    promptId: string;
    confidence: number;
    status: EventPromptStatus;
    description: string;
}

export type UpdateEventsPromptsPayload = Partial<
    Pick<EventsPrompts, 'confidence' | 'description'>
>;

export interface ScoredEvent {
    event: Event;
    confidence: number;
    scores: Record<string, number>;
    focuses: number[][];
}

export interface AlertRuleEvent {
    event: Event;
    promptText: string;
    sourceName: string;
    createdAt: number;
    confidence: number;
    alertRuleId: string;
    focuses: number[][];
}

export interface VehicleEvent {
    event: Event;
    vehicleId: string;
}

export enum DatabaseEventKind {
    NEW_UNIDENTIFIED_PERSON = 'NEW_UNIDENTIFIED_PERSON',
    NEW_UNIDENTIFIED_VEHICLE = 'NEW_UNIDENTIFIED_VEHICLE',
    KNOWN_PERSON_DETECTED = 'KNOWN_PERSON_DETECTED',
    KNOWN_VEHICLE_DETECTED = 'KNOWN_VEHICLE_DETECTED',
}

export interface DatabaseEvent {
    id: ID;
    event: Event;
    kind: DatabaseEventKind;
    entityType: 'person' | 'vehicle';
    entityId: ID;
    entityName: string | null;
    known: boolean;
    confidence: number | null;
    licensePlate: string | null;
    vehicleType: string | null;
    sourceName: string | null;
    createdAt: number;
}

export type DatabaseEventsQueryParams = CollectionQueryParams & {
    sourceId?: string[];
    folderId?: string[];
    kinds?: DatabaseEventKind[];
    entityType?: 'person' | 'vehicle';
    startTime?: number;
    endTime?: number;
};

export type CustomEvent = ReportEventDetail & {
    type: ReportEventType.CUSTOM;
};
export interface EventEntityPerson {
    id: string;
    name: string;
    imageUrl?: string;
    createdAt: number;
    updatedAt: number;
}

export interface EventEntityVehicle {
    vehicle: {
        id: string;
        licensePlate: string;
        vehicleType?: string;
        known: boolean;
        name?: string;
        tags?: string[];
        thumbnailUrl?: string;
        createdAt: number;
    };
    licensePlateText: string;
    similarityScore: number;
    totalEvents: number;
    totalSources: number;
    lastSeen?: number;
}

export interface EventEntityPersonMatch {
    person: EventEntityPerson;
    totalEvents: number;
    totalSources: number;
    lastSeen?: number;
}

export interface EventEntityAlertRule {
    id: string;
    name: string | null;
    promptText: string;
}

export interface EventEntities {
    vehicles: EventEntityVehicle[];
    persons: EventEntityPersonMatch[];
    alertRules: EventEntityAlertRule[];
}
